/**
 * Gemeinsame Erfassungsquelle „Fremd-Tab" (web-tab UND ninox, Issue #61):
 * Die Anwendung läuft in einem eigenen Browser-Tab statt im iframe — der
 * Tester teilt mit ihr weder Origin noch Speicher. Der Screenshot kommt über
 * die Tab-Freigabe (getDisplayMedia, der Melder wählt den Anwendungs-Tab).
 *
 * Seit #108 (Hans 28.09.) ist diese Quelle nur noch der EINSTIEG: Melden
 * heißt Screenshot machen. Mit dem ersten Screenshot legt der Kern die
 * Meldung an und wechselt in die Meldung selbst (Detailansicht im
 * Erfassungsmodus) — es gibt keine Seitenleiste mehr. Die Tab-Verbindung
 * bleibt sitzungsweit (sitzungsCapture), sodass jede weitere Meldung mit
 * einem Klick beginnt.
 *
 * Adapter-spezifisch sind nur Texte, die Öffnen-Buttons und ein optionaler
 * Hinweis-Block — siehe NinoxTabSource (Ninox-Login, Live/DEV) und
 * WebTabSource (SSO, Stage/Live).
 */

import { useEffect, useRef, useState, type ReactNode } from "react"
import { Camera, ExternalLink, MonitorUp, Unplug } from "lucide-react"
import { toast } from "sonner"
import type { AppZiel } from "@/components/AppSwitcher"
import { FremdTabCapture, sitzungsCapture } from "@/lib/screenshot"
import type { Application } from "@/types"

export interface FremdTabTexte {
  /** Erklärung unter dem Anwendungsnamen */
  intro: string
  /** Kurzname des Tabs in Buttons/Badges, z. B. „Ninox-Tab" / „Anwendungs-Tab" */
  tab: string
  /** Beschriftung der Öffnen-Buttons je Ziel */
  oeffnen: { live: string; dev: string; einzig: string }
  /** Erläuterung unter den beiden Zielen (nur wenn `urlLive` gesetzt) */
  zieleHinweis: ReactNode
  /** Schritt 1 der Anleitung (Rest ist gemeinsam) */
  schritt1: ReactNode
  fehlerVerbinden: string
  fehlerVerloren: string
}

export interface FremdTabSourceProps {
  anwendung: Application
  /** Aufgenommenes Bild — der Kern legt damit die Meldung an und wechselt hinein */
  onBild: (bild: Blob) => void
  /** Ersatzweg „Bilddatei hochladen" — öffnet den Datei-Dialog des Kerns */
  onDateiWaehlen: () => void
  /** Anwendung im Fremd-Tab öffnen (openApp-Strategie des Adapters) */
  oeffnen: (ziel: AppZiel) => void
  texte: FremdTabTexte
  /** Optionaler Hinweis-Block unter den Öffnen-Buttons (z. B. Ninox-Login) */
  hinweis?: ReactNode
  /** Capture-Instanz des Adapters (Default: FremdTabCapture) */
  captureErzeugen?: () => FremdTabCapture
  /** Gesetzt = Melden gesperrt, mit diesem Hinweis (Bereitschafts-Gate) */
  gesperrtGrund?: string
  /** Kern arbeitet gerade (Meldung anlegen + Upload) — Knöpfe sperren */
  beschaeftigt?: boolean
}

export function FremdTabSource({
  anwendung,
  onBild,
  onDateiWaehlen,
  oeffnen,
  texte,
  hinweis,
  captureErzeugen,
  gesperrtGrund,
  beschaeftigt = false,
}: FremdTabSourceProps) {
  // Persistente Verbindung zum freigegebenen Tab (F4) — sitzungsweit: einmal
  // verbinden, danach Sofort-Abgriff ohne Dialog, auch für jede weitere
  // Meldung dieser Sitzung. Beim Verlassen NICHT trennen.
  const captureRef = useRef<FremdTabCapture | null>(null)
  const previewRef = useRef<HTMLVideoElement>(null)
  if (captureRef.current === null) {
    captureRef.current = sitzungsCapture(
      `app:${anwendung.id}`,
      captureErzeugen ?? (() => new FremdTabCapture())
    )
  }
  const [tabVerbunden, setTabVerbunden] = useState(() => captureRef.current!.verbunden)
  const [tabVerbindet, setTabVerbindet] = useState(false)

  useEffect(() => {
    const capture = captureRef.current!
    capture.onEnded = () => setTabVerbunden(false)
    return () => {
      capture.onEnded = null
    }
  }, [])

  // Live-Vorschau an das <video> hängen, sobald verbunden
  useEffect(() => {
    if (tabVerbunden && previewRef.current) {
      previewRef.current.srcObject = captureRef.current!.mediaStream
    }
  }, [tabVerbunden])

  const screenshotAufnehmen = async () => {
    if (gesperrtGrund || beschaeftigt) return
    if (!captureRef.current?.verbunden) {
      void tabVerbinden()
      return
    }
    try {
      onBild(await captureRef.current.frameAbgreifen())
    } catch {
      // Verbindung verloren (Tab geschlossen / Teilen beendet) → neu verbinden
      setTabVerbunden(false)
      toast.error(texte.fehlerVerloren)
    }
  }

  // Ein Klick statt drei: Verbinden UND sofort der erste Screenshot — der
  // startet die Meldung.
  const tabVerbinden = async () => {
    if (gesperrtGrund || beschaeftigt) return
    setTabVerbindet(true)
    try {
      await captureRef.current!.verbinden()
      setTabVerbunden(true)
      await screenshotAufnehmen()
    } catch (e) {
      // Abbruch im Freigabe-Dialog ist kein Fehler
      if ((e as Error).name !== "NotAllowedError") toast.error(texte.fehlerVerbinden)
    } finally {
      setTabVerbindet(false)
    }
  }

  const gesperrt = !!gesperrtGrund || beschaeftigt

  return (
    <div className="relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-xl border bg-card">
      <div className="flex h-11 shrink-0 items-center gap-2 border-b bg-muted px-4">
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="h-[9px] w-[9px] rounded-full bg-border" />
        <span className="ml-2 truncate text-xs text-muted-foreground/80">
          {anwendung.url.replace(/^https?:\/\//, "")}
        </span>
      </div>
      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-5 overflow-y-auto px-8 py-10">
        <div className="max-w-xl text-center">
          <h1 className="text-[22px] font-bold">{anwendung.name}</h1>
          <p className="mt-1.5 text-sm text-muted-foreground">{texte.intro}</p>
        </div>
        {anwendung.urlLive ? (
          // Zwei Ziele — Live (echte Daten) und DEV/Stage (Testdaten)
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={() => oeffnen("live")}
                className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
              >
                <ExternalLink className="h-4 w-4" />
                {texte.oeffnen.live}
              </button>
              <button
                type="button"
                onClick={() => oeffnen("dev")}
                className="flex items-center gap-2 rounded-xl bg-secondary px-6 py-3 text-[15px] font-bold hover:bg-secondary/80"
              >
                <ExternalLink className="h-4 w-4" />
                {texte.oeffnen.dev}
              </button>
            </div>
            <p className="text-xs text-muted-foreground">{texte.zieleHinweis}</p>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => oeffnen("dev")}
            className="flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-[15px] font-bold text-primary-foreground shadow-card hover:bg-primary-hover"
          >
            <ExternalLink className="h-4 w-4" />
            {texte.oeffnen.einzig}
          </button>
        )}
        {hinweis}
        <ol className="max-w-xl space-y-2.5 rounded-[10px] border bg-muted/30 px-5 py-4 text-[13px] text-muted-foreground">
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">1.</span>
            <span>{texte.schritt1}</span>
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">2.</span>
            <span>
              <span className="font-semibold text-foreground">
                „{texte.tab} verbinden & Screenshot“
              </span>{" "}
              — der Browser fragt einmal, welcher Tab geteilt wird. Mit dem Screenshot
              entsteht die Meldung, und du bist direkt in ihr.
            </span>
          </li>
          <li className="flex gap-2.5">
            <span className="font-bold text-foreground">3.</span>
            <span>
              In der Meldung: kurzer KI-Dialog, weitere Screenshots, Beschreibung,
              Absenden. Die Verbindung bleibt für alle weiteren Meldungen — beim nächsten
              Mal genügt ein Klick auf die Live-Vorschau.
            </span>
          </li>
        </ol>

        {/* Live-Vorschau des verbundenen Tabs = Auslöser (F4) */}
        {tabVerbunden && (
          <div className="w-full max-w-xl">
            <button
              type="button"
              onClick={() => void screenshotAufnehmen()}
              disabled={gesperrt}
              title={gesperrtGrund ?? `Klicken: Screenshot aufnehmen und Meldung beginnen (${texte.tab})`}
              className="group relative block w-full overflow-hidden rounded-xl border-2 border-primary/30 bg-black shadow-card transition-colors hover:border-primary disabled:cursor-not-allowed disabled:opacity-60"
            >
              <video
                ref={previewRef}
                autoPlay
                muted
                playsInline
                className="aspect-video w-full object-contain"
              />
              <span className="pointer-events-none absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-gradient-to-t from-black/70 to-transparent py-3 text-[13px] font-bold text-white">
                <Camera className="h-4 w-4" />
                {beschaeftigt ? "Meldung wird angelegt …" : "Screenshot aufnehmen — damit beginnt die Meldung"}
              </span>
              <span className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-primary/90 px-2 py-0.5 text-[11px] font-bold text-primary-foreground">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" /> {texte.tab}{" "}
                live
              </span>
            </button>
            <div className="mt-1.5 flex items-center justify-between text-[12px] text-muted-foreground">
              <span>Verbunden — bleibt für alle weiteren Meldungen dieser Sitzung.</span>
              <button
                type="button"
                onClick={() => {
                  captureRef.current?.trennen()
                  setTabVerbunden(false)
                }}
                className="flex items-center gap-1 font-semibold hover:text-foreground"
              >
                <Unplug className="h-3 w-3" /> Verbindung trennen
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Schwebende Melden-Leiste (§8) */}
      <div className="absolute bottom-5 left-1/2 flex -translate-x-1/2 items-center gap-2.5 rounded-full border bg-card px-3.5 py-2.5 shadow-[0_8px_24px_rgba(15,23,42,0.14)]">
        <span className="whitespace-nowrap pl-1 text-[13px] font-semibold">{anwendung.name}</span>
        {tabVerbunden ? (
          <button
            type="button"
            onClick={() => void screenshotAufnehmen()}
            disabled={gesperrt}
            title={gesperrtGrund}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            <Camera className="h-[15px] w-[15px]" />
            {gesperrtGrund ? "Wird vorbereitet …" : beschaeftigt ? "Meldung wird angelegt …" : "Screenshot aufnehmen"}
          </button>
        ) : (
          <button
            type="button"
            onClick={() => void tabVerbinden()}
            disabled={gesperrt || tabVerbindet}
            title={gesperrtGrund}
            className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
          >
            <MonitorUp className="h-[15px] w-[15px]" />
            {gesperrtGrund
              ? "Wird vorbereitet …"
              : tabVerbindet
                ? "Verbinde …"
                : `${texte.tab} verbinden & Screenshot`}
          </button>
        )}
        <button
          type="button"
          onClick={onDateiWaehlen}
          disabled={gesperrt}
          className="whitespace-nowrap rounded-full bg-secondary px-3.5 py-2 text-[13px] font-semibold hover:bg-secondary/80 disabled:opacity-50"
          title="Ersatzweg: Bilddatei hochladen — damit beginnt die Meldung ebenfalls"
        >
          Bild hochladen
        </button>
      </div>
    </div>
  )
}
